// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TRANSLATE_OBJECT } from "@sc/XDL/constants";
import * as React from "react";
import { connect } from "react-redux";

const enum fields {
    pageType = "pageType",
    overrideTitle = "overrideTitle",
    queryString = "queryString",
    testSelector = "testSelector",
    // XNG-Change
    extendedStyles = "extendedStyles",
    onClick = "onClick",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.pageType]: string;
        [fields.overrideTitle]: string;
        [fields.queryString]: string;
        [fields.testSelector]: string;
        // XNG-Change
        [fields.extendedStyles]: LinkPresentationProps;
        [fields.onClick]?: () => void;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    pageLink: getPageLinkByPageType(state, ownProps.fields.pageType),
});

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export const PageTypeLink: React.FC<Props> = ({
    pageLink,
    fields: { queryString, testSelector, overrideTitle, pageType, extendedStyles, onClick },
}) => {
    if (!pageLink) {
        return null;
    }

    const url = queryString ? `${pageLink.url}?${queryString}` : pageLink.url;

    const selector = testSelector;

    // XNG-Changes
    if (pageType.includes("OrderDetailsPage")) {
        extendedStyles = {
            ...extendedStyles,
            typographyProps: { ...(TRANSLATE_OBJECT as any) },
        };
    }

    return (
        <Link
            href={url}
            data-test-selector={selector}
            {...extendedStyles}
            // XNG-Change
            onClick={onClick}
        >
            {overrideTitle || pageLink.title}
        </Link>
    );
};

const pageTypeLink: WidgetModule = {
    component: connect(mapStateToProps)(PageTypeLink),
    definition: {
        group: "Basic",
        icon: "link",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.pageType,
                editorTemplate: "TextField",
                defaultValue: "",
                isRequired: true,
            },
            {
                fieldType: "Translatable",
                name: fields.overrideTitle,
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.queryString,
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.extendedStyles,
                editorTemplate: "TextField",
                defaultValue: "",
                isVisible: () => false,
            },
        ],
    },
};

export default pageTypeLink;
